import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
    getCurrentUser,
    getSession,
    requestSmsVerificationCode,
} from "components/helper/callApi";
import { HTTPResponseError, UnknownError } from "services/errors";
import { useUser } from "components/helper/userContext";
import { useSession } from "components/helper/sessionContext";
import { useToken } from "components/helper/tokenContext";
import SMSVerificationForm from "components/authentificationMode/common/SMSVerificationForm";
import LoginPassword from "components/authentificationMode/LoginPassword";

import { getAdditionalFields } from "services/additionalFields";
import { User } from "types/User";

import { SessionType } from "types/sessionType";
import { Scenario } from "types/scenarioType";

type Props = {
    codeOnline: string;
    scenario: Scenario;
};

const LoginEmailPasswordSMS = ({ codeOnline, scenario }: Props) => {
    const { t } = useTranslation();
    const { setToken } = useToken();
    const userContext = useUser();
    const { setSession } = useSession();
    const navigate = useNavigate();

    const [template, setTemplate] = useState("default");
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
    const [successParams, setSuccessParams] = useState<{
        email?: string;
        password?: string;
    }>({});

    const requestVerificationCode = async (email: string, password: string) => {
        try {
            const phoneNumber: string | null = await requestSmsVerificationCode(
                {
                    email,
                    password,
                    codeOnline,
                },
            );
            setPhoneNumber(phoneNumber);
            return;
        } catch (error) {
            if (!(error instanceof HTTPResponseError)) {
                throw t("errorUnknown");
            }

            const res = (error as HTTPResponseError).response;

            switch (res.status) {
                case 401:
                    throw t("errorCredentialsIncorrect");
                case 429:
                    throw t("errorTooManyRequest");
                case 409:
                    try {
                        const json = await res.json();
                        if (
                            "ONLINE_SCENARIO_NOT_COMPATIBLE" === json.errorCode
                        ) {
                            throw t("scenarioIncorrect");
                        }

                        if ("ONLINE_PHONE_NUMBER_INVALID" === json.errorCode) {
                            throw t("errorPhoneNumberInvalid");
                        }

                        throw t("errorUnknown");
                    } catch {
                        throw t("errorUnknown");
                    }
                default:
                    throw t("errorUnknown");
            }
        }
    };

    const loginUserOnSession = async (token: string) => {
        if (token) {
            setToken(token);
        }

        let userData: User;
        try {
            userData = await getCurrentUser(getAdditionalFields({}), token);
        } catch (error) {
            if (error instanceof HTTPResponseError) {
                throw error;
            }
            throw new UnknownError();
        }

        userContext.setUser(userData);

        let sessionData: SessionType;
        try {
            sessionData = await getSession(codeOnline, ["zoomSignature"]);
        } catch (error) {
            if (!(error instanceof HTTPResponseError)) {
                throw t("errorUnknown");
            }
            switch (error.response.status) {
                case 401:
                    throw t("errorYouMustBeLog");
                case 403:
                    throw t("error403");
                default:
                    throw t("errorUnknown");
            }
        }

        setSession(sessionData);

        navigate(`/session/${codeOnline}`, { replace: true });
    };

    if ("default" === template) {
        return (
            <LoginPassword
                handleSubmit={async (email: string, password: string) => {
                    await requestVerificationCode(email, password);
                    setTemplate("verification-code-form");
                    setSuccessParams({ email, password });
                }}
                codeOnline={codeOnline}
                scenario={scenario}
            />
        );
    }

    return (
        <SMSVerificationForm
            phoneNumber={phoneNumber as string}
            codeOnline={codeOnline}
            scenario={scenario}
            email={successParams.email as string}
            password={successParams.password as string}
            requestVerificationCode={requestVerificationCode}
            onSubmitSuccess={loginUserOnSession}
        />
    );
};

export default LoginEmailPasswordSMS;
